import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UnsubscribePage from './pages/Unsubscribe';
import SalesPage from './pages/SalesPage';
import MemberLoginPage from './pages/MemberLoginPage';
import FreeTrial from './pages/FreeTrial';
import UserDashboard from './pages/UserDashboard';
import MyActions from './pages/MyActions';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import MySummaries from './pages/MySummaries';
import Community from './pages/Community';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminChallenges from './pages/admin/AdminChallenges';
import AdminActions from './pages/admin/AdminActions';
import AdminMilestones from './pages/admin/AdminMilestones';
import AdminTips from './pages/admin/AdminTips';
import AdminAutomations from './pages/admin/AdminAutomations';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminCheckins from './pages/admin/AdminCheckins';
import AdminInvites from './pages/admin/AdminInvites';
import AdminSummaries from './pages/admin/AdminSummaries';
import AdminCommunity from './pages/admin/AdminCommunity';
import AdminProspects from './pages/admin/AdminProspects';
import AdminLeaderboard from './pages/admin/AdminLeaderboard';
import AdminGroupWins from './pages/admin/AdminGroupWins';
import AdminSystemHealth from './pages/admin/AdminSystemHealth';
import AdminAutomationDebug from './pages/admin/AdminAutomationDebug';
import AdminCheckinsMatrix from './pages/admin/AdminCheckinsMatrix';
import AdminActivity from './pages/admin/AdminActivity';
import AdminInbox from './pages/admin/AdminInbox';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ============================================
// GLOBAL CACHE & STATE REFRESH SYSTEM
// ============================================

// Version checker - automatically refresh if server has newer version
const checkForUpdates = async (forceCheck = false) => {
  try {
    // Skip version check if localStorage is not available (e.g., private browsing)
    if (typeof localStorage === 'undefined') {
      console.log('localStorage not available, skipping version check');
      return false;
    }
    
    const storedVersion = localStorage.getItem('app_version');
    const lastCheck = localStorage.getItem('app_version_check');
    const now = Date.now();
    
    // Skip if checked within last 30 seconds (unless forced)
    if (!forceCheck && lastCheck && (now - parseInt(lastCheck)) < 30000) {
      return false;
    }
    
    localStorage.setItem('app_version_check', now.toString());
    
    const res = await axios.get(`${API}/version`, { timeout: 5000 });
    const serverVersion = res.data.version;
    
    if (storedVersion && storedVersion !== serverVersion) {
      // New version available - clear cache and reload
      console.log(`New version available: ${serverVersion}. Refreshing...`);
      localStorage.setItem('app_version', serverVersion);
      
      // Clear any cached data
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
      
      // Clear service worker caches
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          console.log('Cleared all caches');
        } catch (cacheErr) {
          console.log('Failed to clear caches:', cacheErr.message);
        }
      }
      
      // Tell service worker to update
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
      }
      
      window.location.reload(true);
      return true;
    } else if (!storedVersion) {
      localStorage.setItem('app_version', serverVersion);
    }
    return false;
  } catch (err) {
    // Silently fail - don't disrupt user experience
    console.log('Version check failed:', err.message);
    return false;
  }
};

// Global event for triggering data refresh across all components
// Components can listen to this event to refresh their data
export const triggerGlobalRefresh = () => {
  window.dispatchEvent(new Event('globalDataRefresh'));
};

// Hook for components to subscribe to global refresh events
export const useGlobalRefresh = (refreshCallback) => {
  useEffect(() => {
    const handleRefresh = () => {
      if (refreshCallback) refreshCallback();
    };
    window.addEventListener('globalDataRefresh', handleRefresh);
    return () => window.removeEventListener('globalDataRefresh', handleRefresh);
  }, [refreshCallback]);
};

// ============================================
// NAVIGATION LISTENER - Triggers refresh on every page change
// ============================================
const NavigationListener = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    // On every route change:
    // 1. Check for app updates
    checkForUpdates();
    
    // 2. Trigger global data refresh for all listening components
    // Small delay to let the new page mount first
    const timer = setTimeout(() => {
      triggerGlobalRefresh();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);
  
  return children;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// API Client with auth and timeout
export const apiClient = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000, // 15 second timeout to prevent hanging requests
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('rxgrit_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry failed requests once automatically
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config || config._retry) {
      return Promise.reject(error);
    }
    
    // Only retry on network errors or 5xx server errors
    if (!error.response || (error.response.status >= 500 && error.response.status < 600)) {
      config._retry = true;
      await new Promise(r => setTimeout(r, 1000)); // Wait 1 second
      return apiClient(config);
    }
    
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('rxgrit_token');
      localStorage.removeItem('rxgrit_user');
      // Only redirect if not already on login page to prevent loops
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for app updates on load
    checkForUpdates();
    
    const token = localStorage.getItem('rxgrit_token');
    const savedUser = localStorage.getItem('rxgrit_user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token
      apiClient.get('/auth/me')
        .then(res => {
          setUser(res.data);
          localStorage.setItem('rxgrit_user', JSON.stringify(res.data));
        })
        .catch((err) => {
          // Only clear token if it's actually invalid (401 Unauthorized)
          // Don't clear on network errors, timeouts, etc.
          if (err.response?.status === 401) {
            console.log('Token invalid, clearing auth');
            localStorage.removeItem('rxgrit_token');
            localStorage.removeItem('rxgrit_user');
            setUser(null);
          } else {
            // Keep the saved user on network errors - they're still logged in
            console.log('Auth check failed (network issue), keeping saved session:', err.message);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const res = await apiClient.post('/auth/login', { email, password, remember_me: rememberMe });
      
      // Check if localStorage is available
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('rxgrit_token', res.data.token);
        localStorage.setItem('rxgrit_user', JSON.stringify(res.data.user));
      } else {
        console.error('localStorage not available - login may not persist');
      }
      
      setUser(res.data.user);
      return res.data.user;
    } catch (err) {
      console.error('Login error:', err);
      throw err;
    }
  };

  const register = async (name, email, password, timezone = 'America/Vancouver', birthdate = null, inviteCode = null) => {
    const payload = { name, email, password, timezone, invite_code: inviteCode };
    if (birthdate) {
      payload.birthdate = birthdate;
    }
    const res = await apiClient.post('/auth/register', payload);
    localStorage.setItem('rxgrit_token', res.data.token);
    localStorage.setItem('rxgrit_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem('rxgrit_token');
    localStorage.removeItem('rxgrit_user');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await apiClient.get('/auth/me');
      setUser(res.data);
      localStorage.setItem('rxgrit_user', JSON.stringify(res.data));
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F4F1] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NavigationListener>
          <div className="min-h-screen bg-[#F5F4F1]">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/signup" element={<SalesPage />} />
              <Route path="/sales" element={<Navigate to="/signup" replace />} />
              <Route path="/member-login" element={<MemberLoginPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/free-trial" element={<FreeTrial />} />
              
              {/* User Routes */}
              <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
              <Route path="/my-actions" element={<ProtectedRoute><MyActions /></ProtectedRoute>} />
              <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/my-summaries" element={<ProtectedRoute><MySummaries /></ProtectedRoute>} />
              <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/challenges" element={<ProtectedRoute adminOnly><AdminChallenges /></ProtectedRoute>} />
              <Route path="/admin/actions" element={<ProtectedRoute adminOnly><AdminActions /></ProtectedRoute>} />
              <Route path="/admin/milestones" element={<ProtectedRoute adminOnly><AdminMilestones /></ProtectedRoute>} />
              <Route path="/admin/tips" element={<ProtectedRoute adminOnly><AdminTips /></ProtectedRoute>} />
              <Route path="/admin/automations" element={<ProtectedRoute adminOnly><AdminAutomations /></ProtectedRoute>} />
              <Route path="/admin/notifications" element={<ProtectedRoute adminOnly><AdminNotifications /></ProtectedRoute>} />
              <Route path="/admin/invites" element={<ProtectedRoute adminOnly><AdminInvites /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/users/:userId" element={<ProtectedRoute adminOnly><AdminUserDetail /></ProtectedRoute>} />
              <Route path="/admin/checkins" element={<ProtectedRoute adminOnly><AdminCheckins /></ProtectedRoute>} />
              <Route path="/admin/summaries" element={<ProtectedRoute adminOnly><AdminSummaries /></ProtectedRoute>} />
              <Route path="/admin/community" element={<ProtectedRoute adminOnly><AdminCommunity /></ProtectedRoute>} />
              <Route path="/admin/prospects" element={<ProtectedRoute adminOnly><AdminProspects /></ProtectedRoute>} />
              <Route path="/admin/leaderboard" element={<ProtectedRoute adminOnly><AdminLeaderboard /></ProtectedRoute>} />
              <Route path="/admin/group-wins" element={<ProtectedRoute adminOnly><AdminGroupWins /></ProtectedRoute>} />
              <Route path="/admin/system-health" element={<ProtectedRoute adminOnly><AdminSystemHealth /></ProtectedRoute>} />
              <Route path="/admin/activity" element={<ProtectedRoute adminOnly><AdminActivity /></ProtectedRoute>} />
              <Route path="/admin/inbox" element={<ProtectedRoute adminOnly><AdminInbox /></ProtectedRoute>} />
              <Route path="/admin/automation-debug" element={<ProtectedRoute adminOnly><AdminAutomationDebug /></ProtectedRoute>} />
              <Route path="/admin/checkins-matrix" element={<ProtectedRoute adminOnly><AdminCheckinsMatrix /></ProtectedRoute>} />
              
              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </div>
        </NavigationListener>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
